"use client"

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Search, LogOut, Gauge, Info } from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useStytch } from "@stytch/nextjs";
import { useConvexAuth } from "convex/react";
import { useOptionalExam } from "@/contexts/ExamContext";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import SearchModal from "./SearchModal";
import { useAuthModal } from "@/auth/auth-modal-provider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MobileUserMenu } from "@/components/dashboard/MobileUserMenu";
import { ThemeToggle } from "@/components/DashboardTheme";
import { getClientPlan } from "@/lib/plan";
import { useBillingState } from "@/hooks/useBillingState";

const normaliseTimestamp = (timestamp?: number) => {
  if (!timestamp) return undefined;
  return timestamp < 10_000_000_000 ? timestamp * 1000 : timestamp;
};

const formatResetDate = (timestamp?: number) => {
  const normalised = normaliseTimestamp(timestamp);
  if (!normalised) return undefined;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
  }).format(new Date(normalised));
};

const formatResetCountdown = (timestamp?: number) => {
  const normalised = normaliseTimestamp(timestamp);
  if (!normalised) return undefined;
  const diffMs = Math.max(0, normalised - Date.now());
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = Math.max(0, totalMinutes % 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

interface QuestionsInfoContentProps {
  balance: number;
  nextResetAt?: number;
}

const QuestionsInfoContent = memo(function QuestionsInfoContent({ balance, nextResetAt }: QuestionsInfoContentProps) {
  const resetDate = formatResetDate(nextResetAt);
  const resetCountdown = formatResetCountdown(nextResetAt);

  return (
    <div className="space-y-5 p-4 text-sm">
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">Free practice questions</p>
        <p className="text-sm text-muted-foreground">
          You have <span className="font-semibold text-foreground">{balance.toLocaleString()}</span> free practice
          questions left today.
          {resetCountdown ? ` Resets in ${resetCountdown}.` : ""}
        </p>
        {resetDate ? <p className="text-xs text-muted-foreground">Next reset {resetDate} ({resetCountdown}).</p> : null}
        <p className="text-xs text-muted-foreground">
          Upgrade to Exam Pass for unlimited questions, every past paper, and the full textbook.
        </p>
      </div>
      <Button render={<Link href="/dashboard/billing" />} variant="secondary" className="w-full">
        Manage billing
      </Button>
    </div>
  );
});

QuestionsInfoContent.displayName = "QuestionsInfoContent";

interface DashboardHeaderProps {
  isExamSession?: boolean;
  children?: React.ReactNode;
  /** Render without the solid background/border so it floats on the page bg. */
  transparent?: boolean;
  /** Hide the logo on md+ (when the sidebar already shows it). */
  hideLogoOnDesktop?: boolean;
}

export function DashboardHeader({ isExamSession = false, children, transparent = false, hideLogoOnDesktop = false }: DashboardHeaderProps) {
  const stytch = useStytch();
  const signOut = async () => {
    await stytch.session.revoke();
    if (typeof window !== "undefined") window.location.href = "/";
  };
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { openAuthModal } = useAuthModal();
  const pathname = usePathname();
  const router = useRouter();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isDesktopQuestionsPopoverOpen, setDesktopQuestionsPopoverOpen] = useState(false);
  const [isMobileQuestionsPopoverOpen, setMobileQuestionsPopoverOpen] = useState(false);
  const desktopPopoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mobilePopoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { billing } = useBillingState();
  const plan = getClientPlan(billing);
  const hasExamPass = plan.premium;
  const isTextbookPage = pathname.startsWith("/textbook");

  const examContext = useOptionalExam();
  const onExitExam = isExamSession ? examContext?.onExitExam : undefined;
  const questionDetails = useMemo(() => {
    if (!isAuthenticated || hasExamPass) return undefined;

    const nextResetAt = plan.questionNextResetAt ?? undefined;
    return {
      balance: plan.questionBalance,
      nextResetAt,
      nextResetLabel: nextResetAt ? new Date(nextResetAt).toLocaleDateString() : undefined,
    };
  }, [isAuthenticated, hasExamPass, plan]);

  const handleDesktopMouseEnter = () => {
    if (desktopPopoverTimer.current) {
      clearTimeout(desktopPopoverTimer.current);
      desktopPopoverTimer.current = null;
    }
    setDesktopQuestionsPopoverOpen(true);
  };

  const handleDesktopMouseLeave = () => {
    if (desktopPopoverTimer.current) {
      clearTimeout(desktopPopoverTimer.current);
    }
    desktopPopoverTimer.current = setTimeout(() => {
      setDesktopQuestionsPopoverOpen(false);
    }, 100);
  };

  const handleMobileMouseEnter = () => {
    if (mobilePopoverTimer.current) {
      clearTimeout(mobilePopoverTimer.current);
      mobilePopoverTimer.current = null;
    }
    setMobileQuestionsPopoverOpen(true);
  };

  const handleMobileMouseLeave = () => {
    if (mobilePopoverTimer.current) {
      clearTimeout(mobilePopoverTimer.current);
    }
    mobilePopoverTimer.current = setTimeout(() => {
      setMobileQuestionsPopoverOpen(false);
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (desktopPopoverTimer.current) {
        clearTimeout(desktopPopoverTimer.current);
      }
      if (mobilePopoverTimer.current) {
        clearTimeout(mobilePopoverTimer.current);
      }
    };
  }, []);

  const handleSignOut = () => {
    void signOut().catch(console.error);
  };

  // One shared recipe so the search bar, questions pill and Sign Out button have
  // the EXACT same height and curvature. We use plain elements (not the coss
  // <Button>) because its responsive size variants (sm:h-8 etc.) survive a
  // twMerge h-10 override and would desync the heights.
  const controlClass =
    "flex h-10 items-center rounded-lg border border-muted-foreground/20 bg-muted/30 transition-colors hover:bg-muted/50";

  const handleLogoClick = () => {
    if (isExamSession && onExitExam) {
      onExitExam();
    } else {
      // Route based on auth state, avoid duplicate navigation and hard reloads
      if (isLoading) return; // avoid flicker while auth is resolving
      const target = isAuthenticated ? "/dashboard" : "/textbook";
      if (pathname !== target) {
        router.push(target);
      }
    }
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full",
        transparent
          ? "bg-transparent"
          : "border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
      )}
    >
      <div className="flex h-16 items-center justify-between px-3 sm:px-6 w-full">
        {/* Logo */}
        <div className={cn("flex items-center", hideLogoOnDesktop && "md:hidden")}>
          <div
            onClick={handleLogoClick}
            className={"cursor-pointer hover:opacity-80 transition-opacity"}
          >
            <Logo />
          </div>
        </div>

        {/* Right side - Mobile controls */}
        <div className="flex items-center gap-1.5 md:hidden">
          {children}
          {/* Search Icon - Mobile */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden h-10 w-10 rounded-lg bg-muted/30 border border-muted-foreground/20 hover:bg-muted/50"
            onClick={() => setIsSearchModalOpen(true)}
          >
            <Search className="h-5 w-5 text-muted-foreground" />
          </Button>
          {/* Free questions indicator - show only when authenticated and not on Exam Pass */}
          {questionDetails ? (
            <Popover
              open={isMobileQuestionsPopoverOpen}
              onOpenChange={setMobileQuestionsPopoverOpen}
            >
              <div
                className="md:hidden flex h-10 items-center gap-1 rounded-lg bg-secondary pl-3.5 pr-3.5 sm:pr-1"
                onMouseEnter={handleMobileMouseEnter}
                onMouseLeave={handleMobileMouseLeave}
              >
                <Link
                  href="/dashboard/billing"
                  className="flex items-center gap-1.5 text-sm font-semibold tabular-nums text-foreground"
                  aria-label={`View billing, ${questionDetails.balance} free questions available`}
                >
                  <Gauge className="h-4 w-4 text-primary" aria-hidden />
                  {questionDetails.balance.toLocaleString()}
                </Link>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Free questions info"
                    className="hidden sm:inline-flex h-7 w-7 rounded-full text-muted-foreground/70 hover:bg-muted hover:text-foreground"
                    aria-expanded={isMobileQuestionsPopoverOpen}
                  >
                    <Info className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
              </div>
              <PopoverContent
                className="w-96 max-w-sm p-0"
                align="end"
                onMouseEnter={handleMobileMouseEnter}
                onMouseLeave={handleMobileMouseLeave}
              >
                <QuestionsInfoContent
                  balance={questionDetails.balance}
                  nextResetAt={questionDetails.nextResetAt}
                />
              </PopoverContent>
            </Popover>
          ) : null}

          {isLoading ? (
            <div className="w-10 h-10" />
          ) : isTextbookPage && !isAuthenticated ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="hidden sm:inline-flex"
                onClick={() => openAuthModal("signin")}
              >
                Sign In
              </Button>
              <Button size="sm" onClick={() => openAuthModal("signup")}>
                Sign Up
              </Button>
            </>
          ) : isAuthenticated ? (
            <>
              <ThemeToggle />
              <MobileUserMenu />
            </>
          ) : null}
        </div>

        {/* Desktop layout with equal spacing between search, questions, and sign out */}
        <div className="hidden md:flex flex-1 items-center gap-3 ml-6">
          {/* Search Bar - Desktop */}
          <div className="flex-1 min-w-0">
            <button
              type="button"
              className={cn(controlClass, "w-full justify-start px-4 text-sm text-muted-foreground")}
              onClick={() => setIsSearchModalOpen(true)}
            >
              Search past paper questions, topics, or exam papers...
            </button>
          </div>
          {/* Free questions indicator (desktop variant inside) */}
          {questionDetails ? (
            <Popover
              open={isDesktopQuestionsPopoverOpen}
              onOpenChange={setDesktopQuestionsPopoverOpen}
            >
              <div
                className={cn(controlClass, "gap-1.5 pl-3.5 pr-1")}
                title={questionDetails.nextResetLabel ? `Resets ${questionDetails.nextResetLabel}` : undefined}
                onMouseEnter={handleDesktopMouseEnter}
                onMouseLeave={handleDesktopMouseLeave}
              >
                <Link
                  href="/dashboard/billing"
                  className="flex items-center gap-1.5 text-sm font-semibold tabular-nums text-foreground"
                  aria-label={`View billing, ${questionDetails.balance} free questions available`}
                >
                  <Gauge className="h-4 w-4 text-primary" aria-hidden />
                  {questionDetails.balance.toLocaleString()}
                </Link>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Free questions info"
                    className="h-7 w-7 rounded-full text-muted-foreground/70 hover:bg-muted hover:text-foreground"
                    aria-expanded={isDesktopQuestionsPopoverOpen}
                  >
                    <Info className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
              </div>
              <PopoverContent
                className="w-96 max-w-sm p-0"
                align="end"
                onMouseEnter={handleDesktopMouseEnter}
                onMouseLeave={handleDesktopMouseLeave}
              >
                <QuestionsInfoContent
                  balance={questionDetails.balance}
                  nextResetAt={questionDetails.nextResetAt}
                />
              </PopoverContent>
            </Popover>
          ) : null}
          {isAuthenticated ? <ThemeToggle /> : null}
          {/* Sign out / auth buttons (desktop) */}
          {isLoading ? (
            <div className="w-24 h-9" />
          ) : isTextbookPage && !isAuthenticated ? (
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => openAuthModal("signin")}>
                Sign In
              </Button>
              <Button onClick={() => openAuthModal("signup")}>
                Sign Up
              </Button>
            </div>
          ) : isAuthenticated ? (
            <button
              type="button"
              onClick={handleSignOut}
              className={cn(controlClass, "gap-2 px-4 text-sm font-medium text-foreground")}
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden lg:inline">Sign Out</span>
            </button>
          ) : null}
        </div>
      </div>
      <SearchModal isOpen={isSearchModalOpen} onClose={() => setIsSearchModalOpen(false)} />
    </header>
  )
}
